import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  createAssignment,
  getEligibleQuestions,
  listMembers,
  type EligibleQuestion,
  type Member,
} from "../../services/companies-api";

/** FR-004/FR-005/FR-014/FR-015/FR-040/FR-052: pick eligible questions, set a time limit and
 * pass/fail threshold, and warn (non-blocking) if the selected questions span more than one
 * Language tag before assigning.
 *
 * There is no member picker: everyone on the roster with "Takes Test" on gets the test, resolved
 * live at sign-in (FR-052). The list below is shown read-only so the CO Member can see who that
 * is before sending, and change it on the company page if it's wrong. */
export function AssignmentBuilderPage() {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState<EligibleQuestion[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState("");
  const [passFailThresholdPercent, setPassFailThresholdPercent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    getEligibleQuestions(companyId).then(setQuestions);
    listMembers(companyId).then((all) => setMembers(all.filter((m) => m.takesTest)));
  }, [companyId]);

  function toggleQuestion(id: string) {
    setSelectedQuestionIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }

  const selectedLanguages = new Set(
    questions.filter((q) => selectedQuestionIds.includes(q.id)).map((q) => q.language),
  );
  const isMixedLanguage = selectedLanguages.size > 1;

  async function handleSubmit() {
    if (!companyId || selectedQuestionIds.length === 0) return;
    if (isMixedLanguage) {
      const proceed = window.confirm(
        `Selected questions span ${selectedLanguages.size} languages (${Array.from(selectedLanguages).join(", ")}). Assign anyway?`,
      );
      if (!proceed) return;
    }

    setSubmitting(true);
    try {
      await createAssignment(companyId, {
        questionIds: selectedQuestionIds,
        timeLimitMinutes: timeLimitMinutes ? Number(timeLimitMinutes) : null,
        passFailThresholdPercent: passFailThresholdPercent ? Number(passFailThresholdPercent) : null,
      });
      navigate(`/co-member/companies/${companyId}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="p-6">
      <button
        onClick={() => navigate(`/co-member/companies/${companyId}`)}
        className="mb-4 text-sm text-brand"
      >
        ← Back to company
      </button>
      <h1 className="mb-4 font-sans text-xl text-ink">Assign a Test</h1>

      <div className="mb-6 flex gap-4">
        <label className="flex flex-col gap-1 text-sm text-body">
          Time limit (minutes, optional)
          <input
            type="number"
            min="1"
            value={timeLimitMinutes}
            onChange={(e) => setTimeLimitMinutes(e.target.value)}
            className="w-40 rounded border border-surface-alt px-3 py-2"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-body">
          Pass/fail threshold (%, optional)
          <input
            type="number"
            min="0"
            max="100"
            value={passFailThresholdPercent}
            onChange={(e) => setPassFailThresholdPercent(e.target.value)}
            className="w-40 rounded border border-surface-alt px-3 py-2"
          />
        </label>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-6">
        <section>
          <h2 className="mb-2 font-sans text-lg text-ink">
            Eligible questions ({selectedQuestionIds.length} selected)
          </h2>
          {isMixedLanguage && (
            <p className="mb-2 rounded bg-surface-alt px-3 py-2 text-sm text-accent">
              Selected questions span more than one language.
            </p>
          )}
          <div className="flex max-h-96 flex-col gap-1 overflow-y-auto">
            {questions.map((q) => (
              <label key={q.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedQuestionIds.includes(q.id)}
                  onChange={() => toggleQuestion(q.id)}
                />
                <span className="text-body">[{q.language}]</span> {q.text}
              </label>
            ))}
            {questions.length === 0 && (
              <p className="text-sm text-body">No eligible questions for this company's apps yet.</p>
            )}
          </div>
        </section>

        <section>
          <h2 className="mb-2 font-sans text-lg text-ink">
            {t("assignment.whoGetsThis", "Who gets this test")} ({members.length})
          </h2>
          <p className="mb-2 text-sm text-body">
            {t(
              "assignment.whoGetsThisHint",
              "Everyone on the roster with Takes Test on — set that on the company page. Anyone added later is included automatically.",
            )}
          </p>
          <div className="flex max-h-96 flex-col gap-1 overflow-y-auto">
            {members.map((m) => (
              <p key={m.id} className="text-sm text-ink">
                {m.fullName} <span className="text-body">({m.email})</span>
              </p>
            ))}
            {members.length === 0 && (
              <p className="text-sm text-accent">
                {t(
                  "assignment.noTestTakers",
                  "No test-taking members on this company's roster yet — the test can still be created, and they'll pick it up as soon as they're added.",
                )}
              </p>
            )}
          </div>
        </section>
      </div>

      <button
        onClick={handleSubmit}
        disabled={submitting || selectedQuestionIds.length === 0}
        className="rounded bg-brand px-4 py-2 font-sans text-surface disabled:opacity-50"
      >
        {submitting ? "Assigning…" : "Assign test"}
      </button>
    </div>
  );
}
